import { ETLCourse } from './types';

export const DBT_COURSE: ETLCourse = {
  id: 'dbt-fundamentals',
  title: 'dbt (Data Build Tool) Complete Course',
  description: 'Master modern data transformation with dbt Core and dbt Cloud. Learn to build maintainable, testable data pipelines using SQL and version control.',
  level: 'fundamentals',
  totalDuration: '12-16 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'dbt',
  marketDemand: 'high',
  salaryRange: '$85,000 - $140,000',
  prerequisites: [
    'Strong SQL knowledge',
    'Basic understanding of data warehouses',
    'Familiarity with version control (Git)',
    'Command line basics'
  ],
  learningOutcomes: [
    'Set up and configure dbt projects',
    'Build modular, reusable data models',
    'Implement data testing and documentation',
    'Deploy pipelines to production',
    'Optimize dbt performance',
    'Use dbt Cloud for collaboration'
  ],
  modules: [
    {
      id: 'dbt-1',
      title: 'Introduction to dbt and Modern Data Stack',
      description: 'Understanding dbt\'s role in the modern data stack and ELT approach',
      duration: '90 minutes',
      difficulty: 'beginner',
      order: 1,
      objectives: [
        'Understand the ELT vs ETL paradigm shift',
        'Learn dbt\'s core philosophy and benefits',
        'Set up development environment',
        'Create your first dbt project'
      ],
      content: `# Introduction to dbt and Modern Data Stack

## What is dbt?

dbt (data build tool) is a command-line tool that enables data analysts and engineers to transform data in their warehouse more effectively. It allows you to:

- Write modular SQL transformations
- Test your data
- Document your models
- Version control your analytics code

## The Modern Data Stack

### Traditional ETL vs Modern ELT

**Traditional ETL:**
- Extract → Transform → Load
- Complex, monolithic pipelines
- Difficult to maintain and debug

**Modern ELT with dbt:**
- Extract → Load → Transform
- Modular, SQL-based transformations
- Version controlled and testable

## Setting Up Your First dbt Project

### Installation

\`\`\`bash
# Install dbt
pip install dbt-core dbt-postgres

# Initialize project
dbt init my_project
cd my_project
\`\`\`

### Project Structure

\`\`\`
my_project/
├── dbt_project.yml
├── models/
│   ├── staging/
│   ├── intermediate/
│   └── marts/
├── tests/
├── macros/
└── seeds/
\`\`\`

## Your First Model

\`\`\`sql
-- models/staging/stg_customers.sql
{{ config(materialized='view') }}

select
    customer_id,
    first_name,
    last_name,
    email,
    created_at
from {{ ref('raw_customers') }}
where created_at is not null
\`\`\`

## Key Concepts

### 1. Models
- SQL files that represent tables or views
- Use Jinja templating for dynamic SQL
- Reference other models with \`{{ ref() }}\`

### 2. Materializations
- **View**: Virtual table, computed on query
- **Table**: Physical table, pre-computed
- **Incremental**: Only processes new/changed data
- **Ephemeral**: CTE, not materialized

### 3. Sources
- Raw data tables in your warehouse
- Documented and tested entry points

\`\`\`yaml
# models/staging/sources.yml
version: 2

sources:
  - name: jaffle_shop
    tables:
      - name: customers
      - name: orders
\`\`\`

## Best Practices

1. **Layer Your Models**
   - Staging: Clean and standardize raw data
   - Intermediate: Business logic transformations
   - Marts: Final business-ready models

2. **Use Descriptive Names**
   - stg_customers (staging)
   - int_order_items (intermediate)
   - dim_customers (marts)

3. **Document Everything**
   - Add descriptions to models and columns
   - Use dbt docs generate for auto-documentation

## Running dbt

\`\`\`bash
# Run all models
dbt run

# Run specific model
dbt run --select stg_customers

# Generate documentation
dbt docs generate
dbt docs serve
\`\`\`

## Next Steps

In the next module, we'll dive deeper into:
- Advanced modeling techniques
- Testing strategies
- Macro development
- Performance optimization`
    },
    {
      id: 'dbt-2',
      title: 'Advanced dbt Modeling and Testing',
      description: 'Learn advanced modeling patterns, testing strategies, and data quality assurance',
      duration: '2 hours',
      difficulty: 'intermediate',
      order: 2,
      objectives: [
        'Implement advanced modeling patterns',
        'Create comprehensive test suites',
        'Build custom macros and packages',
        'Handle incremental models effectively'
      ],
      content: `# Advanced dbt Modeling and Testing

## Advanced Modeling Patterns

### Slowly Changing Dimensions (SCD)

\`\`\`sql
-- models/marts/dim_customers_scd2.sql
{{ config(
    materialized='incremental',
    unique_key='customer_key',
    on_schema_change='append_new_columns'
) }}

with customer_changes as (
    select
        customer_id,
        first_name,
        last_name,
        email,
        valid_from,
        lead(valid_from, 1, '9999-12-31') over (
            partition by customer_id 
            order by valid_from
        ) as valid_to,
        case 
            when lead(valid_from, 1) over (
                partition by customer_id 
                order by valid_from
            ) is null then true
            else false
        end as is_current
    from {{ ref('stg_customer_history') }}
)

select
    {{ dbt_utils.generate_surrogate_key(['customer_id', 'valid_from']) }} as customer_key,
    customer_id,
    first_name,
    last_name,
    email,
    valid_from,
    valid_to,
    is_current
from customer_changes

{% if is_incremental() %}
    where valid_from > (select max(valid_from) from {{ this }})
{% endif %}
\`\`\`

### Incremental Models

\`\`\`sql
-- models/marts/fct_daily_sales.sql
{{ config(
    materialized='incremental',
    unique_key='date_day',
    on_schema_change='append_new_columns'
) }}

select
    date_trunc('day', order_date) as date_day,
    count(*) as order_count,
    sum(order_amount) as total_revenue,
    avg(order_amount) as avg_order_value
from {{ ref('stg_orders') }}

{% if is_incremental() %}
    where order_date > (select max(date_day) from {{ this }})
{% endif %}

group by 1
\`\`\`

## Testing in dbt

### Built-in Tests

\`\`\`yaml
# models/staging/schema.yml
version: 2

models:
  - name: stg_customers
    description: "Staged customer data"
    columns:
      - name: customer_id
        description: "Primary key for customers"
        tests:
          - unique
          - not_null
      - name: email
        description: "Customer email address"
        tests:
          - unique
          - not_null
          - email_format

  - name: stg_orders
    description: "Staged order data"
    tests:
      - dbt_utils.unique_combination_of_columns:
          combination_of_columns:
            - customer_id
            - order_date
\`\`\`

### Custom Tests

\`\`\`sql
-- tests/assert_positive_order_amounts.sql
select *
from {{ ref('stg_orders') }}
where order_amount <= 0
\`\`\`

### Advanced Testing Patterns

\`\`\`sql
-- tests/assert_revenue_reconciliation.sql
with source_revenue as (
    select sum(amount) as total_amount
    from {{ ref('stg_transactions') }}
),

transformed_revenue as (
    select sum(revenue) as total_revenue
    from {{ ref('fct_daily_sales') }}
)

select *
from source_revenue
cross join transformed_revenue
where abs(total_amount - total_revenue) > 0.01
\`\`\`

## Macros and Packages

### Custom Macros

\`\`\`sql
-- macros/get_column_values.sql
{% macro get_column_values(table_name, column_name) %}
    {% set query %}
        select distinct {{ column_name }}
        from {{ table_name }}
        where {{ column_name }} is not null
    {% endset %}
    
    {% if execute %}
        {% set results = run_query(query) %}
        {% set values = results.columns[0].values() %}
        {{ return(values) }}
    {% else %}
        {{ return([]) }}
    {% endif %}
{% endmacro %}
\`\`\`

### Using dbt Packages

\`\`\`yaml
# packages.yml
packages:
  - package: dbt-labs/dbt_utils
    version: 1.1.1
  - package: calogica/dbt_expectations
    version: 0.10.1
  - package: dbt-labs/audit_helper
    version: 0.8.0
\`\`\`

## Performance Optimization

### Query Optimization

\`\`\`sql
-- Efficient incremental processing
{{ config(
    materialized='incremental',
    unique_key='id',
    incremental_strategy='merge'
) }}

select
    id,
    user_id,
    event_type,
    created_at,
    -- Use partitioning hints
    date(created_at) as event_date
from {{ ref('stg_events') }}

{% if is_incremental() %}
    -- Efficient incremental filter
    where created_at > (
        select coalesce(max(created_at), '1900-01-01')
        from {{ this }}
    )
{% endif %}
\`\`\`

### Model Optimization

1. **Use appropriate materializations**
   - Views for simple transformations
   - Tables for complex aggregations
   - Incremental for large, append-only data

2. **Optimize model dependencies**
   - Minimize deep dependency chains
   - Use ephemeral models for simple transformations

3. **Leverage warehouse features**
   - Clustering keys for better performance
   - Partitioning for time-series data

## Data Quality Framework

### Comprehensive Testing Strategy

\`\`\`yaml
# models/schema.yml
version: 2

models:
  - name: fct_orders
    description: "Order fact table"
    tests:
      - dbt_expectations.expect_table_row_count_to_be_between:
          min_value: 1000
          max_value: 100000
    columns:
      - name: order_amount
        tests:
          - dbt_expectations.expect_column_values_to_be_between:
              min_value: 0
              max_value: 10000
          - dbt_expectations.expect_column_quantile_values_to_be_between:
              quantile: 0.95
              min_value: 100
              max_value: 1000
\`\`\`

## Documentation Best Practices

\`\`\`yaml
version: 2

models:
  - name: dim_customers
    description: |
      Customer dimension table containing current and historical customer information.
      
      This model implements SCD Type 2 to track changes in customer attributes over time.
      
      **Business Rules:**
      - One record per customer per time period
      - is_current flag indicates active record
      - valid_from/valid_to define time boundaries
      
    columns:
      - name: customer_key
        description: "Surrogate key for the customer dimension"
        meta:
          primary_key: true
      - name: customer_id
        description: "Natural key from source system"
        meta:
          business_key: true
\`\`\`

## Next Steps

In the next module, we'll cover:
- dbt Cloud deployment and collaboration
- CI/CD pipelines for dbt projects
- Monitoring and alerting strategies
- Advanced macro development`
    },
    {
      id: 'dbt-3',
      title: 'dbt Cloud and Production Deployment',
      description: 'Deploy dbt projects to production using dbt Cloud and implement CI/CD workflows',
      duration: '2 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Set up dbt Cloud environments',
        'Implement CI/CD workflows',
        'Monitor data pipeline health',
        'Collaborate effectively with teams'
      ],
      content: `# dbt Cloud and Production Deployment

## dbt Cloud Setup

### Environment Configuration

dbt Cloud provides managed infrastructure for running dbt projects with:
- Scheduled job execution
- Web-based IDE
- Collaboration features
- Monitoring and alerting

### Development Environment

\`\`\`yaml
# dbt_project.yml
name: 'analytics'
version: '1.0.0'

profile: 'analytics'

vars:
  # Environment-specific variables
  start_date: '2020-01-01'
  
target-path: "target"
clean-targets:
  - "target"
  - "dbt_packages"

models:
  analytics:
    staging:
      +materialized: view
    intermediate:
      +materialized: ephemeral
    marts:
      +materialized: table
\`\`\`

### Production Configuration

\`\`\`yaml
# profiles.yml (managed by dbt Cloud)
analytics:
  target: prod
  outputs:
    dev:
      type: snowflake
      account: "{{ env_var('SNOWFLAKE_ACCOUNT') }}"
      user: "{{ env_var('DBT_USER') }}"
      password: "{{ env_var('DBT_PASSWORD') }}"
      database: analytics_dev
      warehouse: transforming
      schema: "{{ env_var('DBT_USER') }}"
    prod:
      type: snowflake
      account: "{{ env_var('SNOWFLAKE_ACCOUNT') }}"
      user: "{{ env_var('DBT_SERVICE_USER') }}"
      password: "{{ env_var('DBT_SERVICE_PASSWORD') }}"
      database: analytics_prod
      warehouse: transforming
      schema: analytics
\`\`\`

## CI/CD Workflows

### Git Integration

\`\`\`yaml
# .github/workflows/dbt-ci.yml
name: dbt CI

on:
  pull_request:
    branches: [main]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      
      - name: Set up Python
        uses: actions/setup-python@v4
        with:
          python-version: '3.9'
          
      - name: Install dependencies
        run: |
          pip install dbt-core dbt-snowflake
          dbt deps
          
      - name: Run dbt tests
        run: |
          dbt test --target ci
          
      - name: Check model freshness
        run: |
          dbt source freshness --target ci
\`\`\`

### Deployment Jobs

**Development Job Configuration:**
\`\`\`bash
# Commands for dev deployment
dbt seed --target dev
dbt run --target dev
dbt test --target dev
dbt source freshness --target dev
\`\`\`

**Production Job Configuration:**
\`\`\`bash
# Commands for production deployment
dbt run --target prod
dbt test --target prod
dbt source freshness --target prod
dbt docs generate --target prod
\`\`\`

## Monitoring and Alerting

### Model Freshness Monitoring

\`\`\`yaml
# models/sources.yml
version: 2

sources:
  - name: ecommerce
    description: "E-commerce application database"
    freshness:
      warn_after: {count: 12, period: hour}
      error_after: {count: 24, period: hour}
    loaded_at_field: updated_at
    
    tables:
      - name: orders
        description: "Order transactions"
        freshness:
          warn_after: {count: 1, period: hour}
          error_after: {count: 3, period: hour}
        columns:
          - name: id
            tests:
              - unique
              - not_null
\`\`\`

### Custom Monitoring Macros

\`\`\`sql
-- macros/monitoring.sql
{% macro generate_audit_log() %}
    select
        '{{ this }}' as model_name,
        count(*) as row_count,
        current_timestamp() as run_timestamp,
        '{{ invocation_id }}' as invocation_id
    from {{ this }}
{% endmacro %}

-- Post-hook to track model execution
{{ config(
    post_hook="{{ generate_audit_log() }}"
) }}
\`\`\`

### Data Quality Alerts

\`\`\`sql
-- models/monitoring/data_quality_alerts.sql
with quality_checks as (
    select
        'revenue_anomaly' as check_name,
        case 
            when daily_revenue < (avg_revenue * 0.5) then 'CRITICAL'
            when daily_revenue < (avg_revenue * 0.8) then 'WARNING'
            else 'PASS'
        end as status,
        daily_revenue,
        avg_revenue,
        current_date as check_date
    from (
        select
            sum(revenue) as daily_revenue,
            avg(sum(revenue)) over (
                order by date_day 
                rows between 6 preceding and 1 preceding
            ) as avg_revenue
        from {{ ref('fct_daily_sales') }}
        where date_day = current_date
        group by date_day
    ) daily_stats
)

select *
from quality_checks
where status in ('CRITICAL', 'WARNING')
\`\`\`

## Team Collaboration

### Branch-based Development

1. **Feature Branch Workflow**
   \`\`\`bash
   # Create feature branch
   git checkout -b feature/customer-segmentation
   
   # Develop in dbt Cloud IDE or locally
   # Make changes to models
   
   # Commit and push
   git add .
   git commit -m "Add customer segmentation model"
   git push origin feature/customer-segmentation
   
   # Create pull request
   \`\`\`

2. **Code Review Process**
   - Automated testing via CI/CD
   - Peer review of SQL logic
   - Documentation review
   - Performance impact assessment

### Documentation Standards

\`\`\`yaml
# models/schema.yml
version: 2

models:
  - name: fct_customer_orders
    description: |
      **Business Purpose**: Customer order fact table for analysis
      
      **Data Sources**: 
      - Orders from {{ doc("orders_source") }}
      - Customers from {{ doc("customers_source") }}
      
      **Update Frequency**: Daily at 6 AM UTC
      
      **Business Rules**:
      - Excludes cancelled orders
      - Includes only paid orders
      - Customer info as of order date
      
      **Known Issues**:
      - Historical data before 2020 may have quality issues
      - International orders may have currency conversion delays
      
    meta:
      owner: "data-team@company.com"
      tags: ["finance", "daily"]
      
    columns:
      - name: order_key
        description: "Surrogate key for orders"
        meta:
          dimension: false
          measure: false
\`\`\`

## Advanced Production Patterns

### Environment-specific Variables

\`\`\`yaml
# dbt_project.yml
vars:
  # Development values
  lookback_days: 30
  min_order_amount: 1
  
  # Production overrides
  prod:
    lookback_days: 365
    min_order_amount: 10
\`\`\`

### Model Contracts

\`\`\`yaml
# models/schema.yml
version: 2

models:
  - name: dim_customers
    config:
      contract:
        enforced: true
    columns:
      - name: customer_id
        data_type: varchar(50)
        constraints:
          - type: not_null
          - type: primary_key
      - name: email
        data_type: varchar(255)
        constraints:
          - type: not_null
          - type: unique
\`\`\`

### Performance Monitoring

\`\`\`sql
-- models/monitoring/model_performance.sql
with model_runtime as (
    select
        model_name,
        execution_time_seconds,
        rows_affected,
        execution_time_seconds / nullif(rows_affected, 0) as seconds_per_row,
        run_date
    from {{ ref('dbt_run_results') }}
    where run_date >= current_date - 7
)

select
    model_name,
    avg(execution_time_seconds) as avg_runtime_seconds,
    max(execution_time_seconds) as max_runtime_seconds,
    avg(seconds_per_row) as avg_seconds_per_row
from model_runtime
group by model_name
having avg_runtime_seconds > 300  -- Flag long-running models
order by avg_runtime_seconds desc
\`\`\`

## Next Steps

You now have a complete dbt foundation! Consider exploring:
- Advanced analytics engineering patterns
- dbt package development
- Integration with BI tools
- Data governance frameworks
- Machine learning model deployment`
    }
  ]
};